/**
 * Role-based access permissions - Single source of truth.
 *
 * Terminology:
 *   - Portal administrator — set publicMetadata.role to "admin" (PERMISSIONS.ADMIN). Full access; only this level can send invitations.
 *   - Module access — set publicMetadata.permissions to an array of keys below (non-admins).
 *
 * Set user.publicMetadata in Clerk Dashboard:
 *   - role: "admin"  → full access (only role that can send invitations)
 *   - permissions: ["group-data", "step-study", "inventory", "dashboard", "review"]  → custom access
 *
 * To add a new module:
 * 1. Add route(s) to ROUTES below, including `section` (flywheel area id)
 * 2. Set showInSection: true and a sectionOrder when it should appear on that area's landing page
 * 3. Add backend route in server.js with requirePermission([...])
 * 4. Add permission constant to backend/config/permissions.js if new
 *
 * The bottom nav is Home, the four flywheel sections, and More.
 * It is NOT built from showInNav. Give new tools a section instead of a nav icon.
 *
 * Leaders survey admin pages use section: 'leaders' and sectionOrder 40–49
 * so they sit after My Survey and before Ministry Checkup.
 */

export const PERMISSIONS = {
  ADMIN: 'admin',
  GROUP_DATA: 'group-data',
  STEP_STUDY: 'step-study',
  INVENTORY: 'inventory',
  DASHBOARD: 'dashboard',
  REVIEW: 'review',
  SUMMIT_FEEDBACK: 'summit-feedback',
};

/** Routes that require no permission (all signed-in users). See also public paths in App.js (/faqs, /accessibility, /how-to). */
export const PUBLIC_ROUTES = [
  '/',
  '/leaders',
  '/gatherings',
  '/studies',
  '/outreach-hub',
  '/admin-area',
  '/help',
  '/faqs',
  '/accessibility',
  '/how-to',
  '/help-videos',
];

/**
 * Flywheel areas plus the two areas that live under More.
 * kind: 'flywheel' is a bottom-nav section. kind: 'more' is reached from More.
 * comingSoon is plain text for the landing page. It is not a link or a button.
 */
export const AREAS = [
  {
    id: 'leaders',
    kind: 'flywheel',
    letter: 'A',
    path: '/leaders',
    navLabel: 'Leaders',
    title: 'Leader & Servant Development',
    description: 'Meetings, surveys, and training for people who serve.',
    comingSoon: 'Coming soon: monthly servant training, prayer circle updates, one-to-one coaching, and servant scheduling.',
  },
  {
    id: 'gatherings',
    kind: 'flywheel',
    letter: 'B',
    path: '/gatherings',
    navLabel: 'Gatherings',
    title: 'Recovery Gatherings',
    description: 'Attendance, reports, and inventory for gatherings.',
    comingSoon: 'Coming soon: greeters, prayer team, chip ceremony, and an information table.',
  },
  {
    id: 'studies',
    kind: 'flywheel',
    letter: 'C',
    path: '/studies',
    navLabel: 'Studies',
    title: 'Recovery Studies',
    description: 'Study tracking, cohorts, and progress.',
    comingSoon: 'Coming soon: The Journey Begins, The Journey Continues, and a Discovery Bible Study.',
  },
  {
    id: 'outreach',
    kind: 'flywheel',
    letter: 'D',
    path: '/outreach-hub',
    navLabel: 'Outreach',
    title: 'Ministry Outreach',
    description: 'Ways to invite people in.',
    comingSoon: 'Coming soon: church referrals and a weekly newsletter.',
  },
  {
    id: 'admin',
    kind: 'more',
    letter: null,
    path: '/admin-area',
    navLabel: 'Admin',
    title: 'Admin',
    description: 'Invitations and who can open which tools.',
    comingSoon: null,
  },
  {
    id: 'help',
    kind: 'more',
    letter: null,
    path: '/help',
    navLabel: 'Help',
    title: 'Help',
    description: 'Answers, getting started, and accessibility.',
    comingSoon: null,
  },
];

export const FLYWHEEL_SECTIONS = AREAS.filter((area) => area.kind === 'flywheel');

/** Help pages live outside ROUTES (they are public). Listed on the Help landing page and in More. */
export const HELP_LINKS = [
  { path: '/faqs', label: 'FAQs', blurb: 'Common questions about the portal.' },
  { path: '/how-to', label: 'How to', blurb: 'Invitations, sign-up, and first steps.' },
  { path: '/accessibility', label: 'Accessibility', blurb: 'How this portal supports access.' },
];

const AREA_BY_ID = Object.fromEntries(AREAS.map((area) => [area.id, area]));

export function getArea(sectionId) {
  return AREA_BY_ID[sectionId] || null;
}

/** PageHeader back link for a tool that belongs to a section. */
export function sectionBackProps(sectionId) {
  const area = getArea(sectionId);
  if (!area) return { backTo: '/', backLabel: '← Home' };
  return { backTo: area.path, backLabel: `← ${area.navLabel}` };
}

/**
 * ROUTES - Single source of truth for all protected routes.
 * path: route path (use :id for dynamic segments). Keep existing paths so bookmarks keep working.
 * permission: required permission (null = all authenticated users)
 * componentKey: key for component lookup in App.js
 * sectionPageId: render the shared section landing page for this area
 * label: display label for nav and the document title
 * section: flywheel or more-menu area id. Omit for routes that are not part of an area.
 * showInSection: list the route on that area's landing page
 * sectionOrder: sort order on the landing page. 40–49 is reserved for leaders-survey admin pages.
 * sectionAudience: extra visibility rule.
 *   'admin-or-assigned-checkup' matches the checkup link rules.
 *   'assigned-leaders-survey' shows My Survey only after a module is assigned.
 * blurb: one line under the tool link on the landing page
 * linkLabel: landing-page label when `label` is too short
 * sectionLabel: optional legacy group heading (PERMISSION_ROUTES)
 * navIcon / homeIcon: legacy icon keys
 * showInNav: legacy flag. The flywheel bar does not read this.
 * showOnHome: legacy flag for PERMISSION_ROUTES
 * end: NavLink exact match (true for / only)
 */
export const ROUTES = [
  {
    path: '/',
    permission: null,
    componentKey: 'Home',
    label: 'Home',
    sectionLabel: null,
    navIcon: 'home',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/leaders',
    permission: null,
    componentKey: 'SectionPage',
    sectionPageId: 'leaders',
    label: 'Leader & Servant Development',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/gatherings',
    permission: null,
    componentKey: 'SectionPage',
    sectionPageId: 'gatherings',
    label: 'Recovery Gatherings',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/studies',
    permission: null,
    componentKey: 'SectionPage',
    sectionPageId: 'studies',
    label: 'Recovery Studies',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/outreach-hub',
    permission: null,
    componentKey: 'SectionPage',
    sectionPageId: 'outreach',
    label: 'Ministry Outreach',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/admin-area',
    permission: null,
    componentKey: 'SectionPage',
    sectionPageId: 'admin',
    label: 'Admin',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/help',
    permission: null,
    componentKey: 'SectionPage',
    sectionPageId: 'help',
    label: 'Help',
    showInNav: false,
    showOnHome: false,
    end: true,
  },
  {
    path: '/help-videos',
    permission: null,
    componentKey: 'HelpVideosPage',
    label: 'Help Videos',
    sectionLabel: null,
    navIcon: 'videos',
    homeIcon: 'videos',
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 80,
    blurb: 'Short explainers for using this portal.',
  },
  {
    path: '/form',
    permission: PERMISSIONS.GROUP_DATA,
    componentKey: 'CRForm',
    label: 'Form',
    sectionLabel: 'Ministry Data',
    navIcon: 'addData',
    homeIcon: 'addData',
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'gatherings',
    showInSection: false,
  },
  {
    path: '/dynamic-entry',
    permission: PERMISSIONS.GROUP_DATA,
    componentKey: 'DynamicEntryPage',
    label: 'Add Group Data',
    sectionLabel: 'Ministry Data',
    navIcon: 'addData',
    homeIcon: 'addData',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'gatherings',
    showInSection: true,
    sectionOrder: 10,
    blurb: 'Enter attendance and counts for a gathering.',
  },
  {
    path: '/thank-you',
    permission: PERMISSIONS.GROUP_DATA,
    componentKey: 'ThankYouPage',
    label: 'Thank You',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'gatherings',
    showInSection: false,
  },
  {
    path: '/review',
    permission: PERMISSIONS.REVIEW,
    componentKey: 'ReviewPage',
    label: 'Ministry Lead Review',
    sectionLabel: null,
    navIcon: 'review',
    homeIcon: 'review',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'gatherings',
    showInSection: true,
    sectionOrder: 20,
    blurb: 'Review entries and submit the gathering report.',
  },
  {
    path: '/dashboard',
    permission: PERMISSIONS.DASHBOARD,
    componentKey: 'CRDashboard',
    label: 'Reports Summary',
    sectionLabel: null,
    navIcon: 'dashboard',
    homeIcon: 'dashboard',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'gatherings',
    showInSection: true,
    sectionOrder: 30,
    blurb: 'Recent reports and yearly totals.',
  },
  {
    path: '/graphs',
    permission: PERMISSIONS.DASHBOARD,
    componentKey: 'GraphsPage',
    label: 'Graphs',
    sectionLabel: null,
    navIcon: 'graphs',
    homeIcon: 'graphs',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'gatherings',
    showInSection: true,
    sectionOrder: 40,
    blurb: 'Charts for attendance and trends.',
  },
  {
    path: '/inventory',
    permission: PERMISSIONS.INVENTORY,
    componentKey: 'InventoryPage',
    label: 'Inventory',
    homeLabel: 'Submit Inventory',
    sectionLabel: 'Inventory',
    navIcon: 'inventory',
    homeIcon: 'inventory',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'gatherings',
    showInSection: true,
    sectionOrder: 50,
    blurb: 'Monthly count of chips, coins, and books.',
  },
  {
    path: '/inventory/history',
    permission: PERMISSIONS.INVENTORY,
    componentKey: 'InventoryHistoryPage',
    label: 'Inv History',
    linkLabel: 'Inventory History',
    homeLabel: 'View Inventory History',
    sectionLabel: 'Inventory',
    navIcon: 'history',
    homeIcon: 'history',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'gatherings',
    showInSection: true,
    sectionOrder: 60,
    blurb: 'Past inventory reports.',
  },
  {
    path: '/step-study',
    permission: PERMISSIONS.STEP_STUDY,
    componentKey: 'StepStudyDashboard',
    label: 'Step Study',
    linkLabel: 'Step Study Tracking',
    homeLabel: 'Step Study Tracking',
    sectionLabel: 'Step Study',
    navIcon: 'study',
    homeIcon: 'study',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'studies',
    showInSection: true,
    sectionOrder: 10,
    blurb: 'Cohorts, attendance, and graduates.',
  },
  {
    path: '/step-study/new',
    permission: PERMISSIONS.STEP_STUDY,
    componentKey: 'StepStudyNewCohort',
    label: 'New Cohort',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'studies',
    showInSection: false,
  },
  {
    path: '/step-study/:id',
    permission: PERMISSIONS.STEP_STUDY,
    componentKey: 'StepStudyCohortPage',
    label: 'Cohort',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: false,
    section: 'studies',
    showInSection: false,
  },
  {
    path: '/invite',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'InvitePage',
    label: 'Invite User',
    sectionLabel: 'Admin',
    navIcon: 'invite',
    homeIcon: 'invite',
    showInNav: false,
    showOnHome: true,
    homeOrder: 3,
    end: true,
    section: 'admin',
    showInSection: true,
    sectionOrder: 10,
    blurb: 'Send an invitation to join the portal.',
  },
  {
    path: '/admin/meetings/issues',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'HotIssueCapturePage',
    label: 'Issues to Discuss',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 20,
    blurb: 'Capture items to bring up at the next meeting.',
  },
  {
    path: '/admin/meetings',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'MeetingsPage',
    label: 'Ministry Meetings',
    sectionLabel: 'Admin',
    navIcon: 'calendar',
    homeIcon: 'calendar',
    showInNav: false,
    showOnHome: true,
    homeOrder: 1,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 10,
    blurb: 'Agenda, notes, and follow-up.',
  },
  {
    path: '/admin/outreach',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'OutreachPage',
    label: 'Outreach',
    sectionLabel: 'Admin',
    navIcon: 'outreach',
    homeIcon: 'outreach',
    showInNav: false,
    showOnHome: true,
    homeOrder: 2,
    end: true,
    section: 'outreach',
    showInSection: true,
    sectionOrder: 10,
    blurb: 'Opportunities to invite people in.',
  },
  {
    path: '/admin/access',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'AdminAccessPage',
    label: 'Access & roles',
    homeLabel: 'Access & roles',
    sectionLabel: 'Admin',
    navIcon: 'accessGuide',
    homeIcon: 'accessGuide',
    showInNav: false,
    showOnHome: true,
    homeOrder: 4,
    end: true,
    section: 'admin',
    showInSection: true,
    sectionOrder: 20,
    blurb: 'See which permission opens which page.',
  },
  {
    path: '/ministry-checkup',
    permission: null,
    componentKey: 'MinistryCheckupPage',
    label: 'Ministry Checkup',
    sectionLabel: 'Ministry Checkup',
    navIcon: 'checkup',
    homeIcon: 'checkup',
    showInNav: false,
    showOnHome: false,
    showOnHomeForPermissions: [PERMISSIONS.ADMIN],
    homeOrder: 0,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 50,
    sectionAudience: 'admin-or-assigned-checkup',
    blurb: 'A health survey for people who were invited.',
  },
  {
    path: '/ministry-checkup/new',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'MinistryCheckupNewPage',
    label: 'New Checkup',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: false,
  },
  {
    path: '/ministry-checkup/:id/take',
    permission: null,
    componentKey: 'MinistryCheckupTakePage',
    label: 'Take Survey',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: false,
  },
  {
    path: '/ministry-checkup/:id/completion',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'MinistryCheckupCompletionPage',
    label: 'Completion',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: false,
  },
  {
    path: '/ministry-checkup/:id/results',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'MinistryCheckupResultsPage',
    label: 'Results',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: false,
  },
  {
    path: '/my-survey',
    permission: null,
    componentKey: 'LeadersSurveyPage',
    label: 'My Survey',
    sectionLabel: null,
    navIcon: 'survey',
    homeIcon: 'survey',
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 30,
    sectionAudience: 'assigned-leaders-survey',
    blurb: 'Answer the questions assigned to you.',
  },
  {
    path: '/admin/survey',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'SurveyAssignmentsPage',
    label: 'Survey assignments',
    homeLabel: 'Survey assignments',
    sectionLabel: 'Admin',
    navIcon: 'survey',
    homeIcon: 'survey',
    showInNav: false,
    showOnHome: true,
    homeOrder: 6,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 40,
    blurb: 'Choose which modules each person should answer.',
  },
  {
    path: '/admin/survey/progress',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'SurveyProgressPage',
    label: 'Survey progress',
    homeLabel: 'Survey progress',
    sectionLabel: 'Admin',
    navIcon: null,
    showInNav: false,
    showOnHome: true,
    homeOrder: 7,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 41,
    blurb: 'See who has started, without opening their answers.',
  },
  {
    path: '/admin/survey-edits',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'SurveyEditsPage',
    label: 'Minor edits',
    homeLabel: 'Minor edits',
    sectionLabel: 'Admin',
    navIcon: 'survey',
    homeIcon: 'survey',
    showInNav: false,
    showOnHome: true,
    homeOrder: 8,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 42,
    blurb: 'Edit question text while the survey is open.',
  },
  {
    path: '/admin/survey/results',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'SurveyResultsPage',
    label: 'Survey results',
    homeLabel: 'Survey results',
    sectionLabel: 'Admin',
    navIcon: null,
    homeIcon: 'survey',
    showInNav: false,
    showOnHome: true,
    homeOrder: 9,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 43,
    blurb: 'Grouped totals only. Individual answers stay hidden.',
  },
  {
    path: '/summit-feedback',
    permission: PERMISSIONS.SUMMIT_FEEDBACK,
    componentKey: 'SummitFeedbackPage',
    label: 'Summit Feedback',
    sectionLabel: 'Summit Feedback',
    navIcon: 'summit',
    homeIcon: 'summit',
    showInNav: false,
    showOnHome: true,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 60,
    blurb: 'Share feedback from the annual summit.',
  },
  {
    path: '/summit-feedback/:yearId/workshop/:workshopId',
    permission: PERMISSIONS.SUMMIT_FEEDBACK,
    componentKey: 'SummitFeedbackFormPage',
    label: 'Workshop Feedback',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: false,
  },
  {
    path: '/admin/summit-feedback',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'SummitFeedbackAdminPage',
    label: 'Summit Feedback Admin',
    homeLabel: 'Summit Feedback',
    sectionLabel: 'Admin',
    navIcon: 'summit',
    homeIcon: 'summit',
    showInNav: false,
    showOnHome: true,
    homeOrder: 5,
    end: true,
    section: 'leaders',
    showInSection: true,
    sectionOrder: 70,
    blurb: 'Set up summit feedback and read results.',
  },
  {
    path: '/admin/summit-feedback/:yearId/results',
    permission: PERMISSIONS.ADMIN,
    componentKey: 'SummitFeedbackResultsPage',
    label: 'Summit Results',
    sectionLabel: null,
    navIcon: null,
    showInNav: false,
    showOnHome: false,
    end: true,
    section: 'leaders',
    showInSection: false,
  },];

/** Map path patterns to required permission (for useRoutePermission path matching) */
export const ROUTE_PERMISSIONS = Object.fromEntries(
  ROUTES.map((r) => [r.path, r.permission])
);

/**
 * Permission -> routes for older home groupings (filtered by showOnHome).
 * The flywheel home uses section + showInSection instead.
 */
export const PERMISSION_ROUTES = (() => {
  const map = {};
  for (const route of ROUTES) {
    const perms = [];
    if (route.showOnHome && route.permission) perms.push(route.permission);
    if (route.showOnHomeForPermissions) perms.push(...route.showOnHomeForPermissions);
    for (const p of perms) {
      if (!map[p]) map[p] = [];
      map[p].push({
        path: route.path,
        label: route.homeLabel ?? route.label,
        sectionLabel: route.sectionLabel,
        homeIcon: route.homeIcon,
        homeOrder: route.homeOrder ?? 999,
      });
    }
  }
  for (const key of Object.keys(map)) {
    map[key].sort((a, b) => (a.homeOrder ?? 999) - (b.homeOrder ?? 999));
  }
  return map;
})();

/** Bottom nav: Home, flywheel sections, More. Section items are hidden when the viewer has no tools there. */
export const NAV_CONFIG = [
  { id: 'home', path: '/', label: 'Home', end: true },
  ...FLYWHEEL_SECTIONS.map((section) => ({
    id: section.id,
    path: section.path,
    label: section.navLabel,
    letter: section.letter,
    title: section.title,
    end: false,
  })),
  { id: 'more', path: null, label: 'More' },
];

export function toolLinkLabel(route) {
  return route.linkLabel || route.label;
}

/**
 * Whether a landing-page tool should be listed for this viewer.
 * access: { isAdmin, hasPermission, hasAssignedCheckup, hasLeadersSurvey }
 */
export function canSeeListedRoute(route, access) {
  if (!route?.showInSection || !route.section) return false;
  const isAdmin = Boolean(access?.isAdmin || access?.hasPermission?.(PERMISSIONS.ADMIN));
  if (route.sectionAudience === 'admin-or-assigned-checkup') {
    return isAdmin || Boolean(access?.hasAssignedCheckup);
  }
  if (route.sectionAudience === 'assigned-leaders-survey') {
    return Boolean(access?.hasLeadersSurvey);
  }
  if (route.permission == null) return true;
  return Boolean(access?.hasPermission?.(route.permission));
}

/** Tools to list on a section landing page, in sectionOrder. Pass `routes` to include not-yet-merged pages. */
export function visibleTools(sectionId, access, routes = ROUTES) {
  return routes
    .filter((route) => route.section === sectionId && canSeeListedRoute(route, access))
    .sort((a, b) => (a.sectionOrder ?? 999) - (b.sectionOrder ?? 999));
}

export function sectionHasVisibleTools(sectionId, access, routes = ROUTES) {
  if (sectionId === 'help') return true;
  return visibleTools(sectionId, access, routes).length > 0;
}

export function visibleFlywheelSections(access, routes = ROUTES) {
  return FLYWHEEL_SECTIONS.filter((section) => sectionHasVisibleTools(section.id, access, routes));
}

export function visibleMoreAreas(access, routes = ROUTES) {
  return AREAS.filter((area) => area.kind === 'more' && sectionHasVisibleTools(area.id, access, routes));
}
